import {
  Box,
  HStack,
  Table,
  TableContainer,
  type TableProps,
} from "@chakra-ui/react";
import { TableBody } from "@src/components/ui-kit/DataTable/Body/components/body";
import { TableFooter } from "@src/components/ui-kit/DataTable/Body/components/footer";
import { TableHeader } from "@src/components/ui-kit/DataTable/Body/components/header";
import {
  ACTIONS_COLUMN_ID,
  EXPANDER_COLUMN_ID,
  EXPANDER_COLUMN_SIZE,
  SYSTEM_COLUMN_IDS,
  useTableBodyColumns,
} from "@src/components/ui-kit/DataTable/Body/hooks/columns";
import { useTableBodyOrder } from "@src/components/ui-kit/DataTable/Body/hooks/order";
import { useTableBodyPinning } from "@src/components/ui-kit/DataTable/Body/hooks/pinning";
import { useTableBodySettings } from "@src/components/ui-kit/DataTable/Body/hooks/settings";
import { useTableBodyStoreSync } from "@src/components/ui-kit/DataTable/Body/hooks/store-sync";
import { BulkActions } from "@src/components/widgets/bulk-actions";
import type { Row as TanstackRowType } from "@tanstack/react-table";
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { BodyProps, BodyRow } from "../types";
import {
  ColumnVisibilityControl,
  TruncatedTextWithTooltip,
} from "./components";
import {
  getBooleanRecordStateKey,
  getColumnSizeCssVar,
  getHeaderSizeCssVar,
  getTableRowId,
} from "./helpers";
import { DataTableBodyStore } from "./store";

export const Body = observer(function Body<
  Row extends BodyRow,
  OrderByColumn = unknown,
>({
  tableKey,
  data,
  columns,
  orderedBy,
  onOrderByChange,
  bulkActions,
  disableBulkActions = false,
  selected,
  onSelect,
  onAfterSelectAll,
  allIds,
  enableRowSelection = true,
  enableSubRowSelection = true,
  onExpandRow,
  onExpandAllRows,
  enableColumnVisibilityControl = true,
  enableColumnResizing = true,
  enableColumnOrdering = true,
  rowSelectionMode,
  beforeVisibilityControl,
  showOnlySubRows = false,
  hideExpandColumn = false,
  defaultHiddenColumnIds,
  initialExpanded = false,
  emptyState,
  tableContainerSx,
  bulkActionsInfoBlock,
}: BodyProps<Row, OrderByColumn>) {
  const isRowSelectionEnabled =
    onSelect !== undefined && enableRowSelection !== false;
  const isSubRowSelectionEnabled =
    isRowSelectionEnabled && enableSubRowSelection !== false;

  const defaultHiddenColumnIdsKey = useMemo(
    () => (defaultHiddenColumnIds ?? []).slice().toSorted().join("|"),
    [defaultHiddenColumnIds],
  );

  const tanstackBodyStore = useMemo(
    () =>
      new DataTableBodyStore({
        tableKey,
        bulkActions: bulkActions,
        allIds,
        onSelect,
        onAfterSelectAll,
        defaultHiddenColumnIds,
      }),
    [defaultHiddenColumnIdsKey, tableKey],
  );

  const hasSubRows = data.some((row) => (row.__subRows?.length ?? 0) > 0);

  const pageSelectableIds = useMemo(() => {
    if (
      rowSelectionMode === "subrows" ||
      rowSelectionMode === "subrowsOrParent"
    ) {
      return data.flatMap(
        (row) => row.__subRows?.map((subRow) => subRow.id) ?? [],
      );
    }

    return data.map((row) => row.id);
  }, [data, rowSelectionMode]);

  const resolveCanSelectRow = useCallback(
    (row: TanstackRowType<Row>): boolean => {
      if (!isRowSelectionEnabled) {
        return false;
      }

      if (
        rowSelectionMode === "subrows" ||
        rowSelectionMode === "subrowsOrParent" ||
        rowSelectionMode === "subrowsOrEmptyParent"
      ) {
        return true;
      }

      if (!enableSubRowSelection && row.depth > 0) {
        return false;
      }

      return true;
    },
    [enableSubRowSelection, isRowSelectionEnabled, rowSelectionMode],
  );

  const {
    tableColumns,
    columnLabelById,
    hideableDataColumnIds,
    dataColumnIds,
    dataColumnIdsSet,
  } = useTableBodyColumns({
    columns,
    tanstackBodyStore,
    pageSelectableIds,
    rowSelectionMode,
    hasSubRows,
    hideExpandColumn,
    onExpandRow,
    onExpandAllRows,
    isRowSelectionEnabled,
  });

  const {
    columnOrder,
    dataColumnOrder,
    onColumnOrderChange,
    hasCustomColumnOrder,
  } = useTableBodyOrder({
    enableColumnOrdering,
    tanstackBodyStore,
    isRowSelectionEnabled,
    hasSubRows,
    hideExpandColumn,
    dataColumnIds,
    dataColumnIdsSet,
  });

  const {
    columnVisibility,
    onColumnVisibilityChange,
    onResetColumnPreferences,
    isResetColumnPreferencesDisabled,
  } = useTableBodySettings({
    tanstackBodyStore,
    hasCustomColumnOrder,
    dataColumnIds,
    hideableDataColumnIds,
  });

  const { columnPinning, onColumnPinningChange } = useTableBodyPinning({
    tanstackBodyStore,
    dataColumnIds,
    dataColumnIdsSet,
  });

  const table = useReactTable<Row>({
    data,
    columns: tableColumns,
    state: {
      expanded: tanstackBodyStore.expanded,
      rowSelection: tanstackBodyStore.rowSelection,
      columnVisibility,
      columnOrder,
      columnSizing: tanstackBodyStore.columnSizing,
      columnPinning,
    },
    enableRowSelection: resolveCanSelectRow,
    enableSubRowSelection:
      isSubRowSelectionEnabled &&
      (rowSelectionMode === undefined || rowSelectionMode === "rows") &&
      enableSubRowSelection,
    enableColumnResizing,
    columnResizeMode: "onChange",
    getRowId: getTableRowId,
    getSubRows: (row) => (row.__subRows as Row[] | undefined) ?? [],
    getRowCanExpand: (row) => Boolean(row.original.__subRows?.length),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: tanstackBodyStore.updateExpanded,
    onRowSelectionChange: tanstackBodyStore.updateRowSelection,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnSizingChange: tanstackBodyStore.updateColumnSizing,
    onColumnPinningChange,
  });

  useTableBodyStoreSync({
    store: tanstackBodyStore,
    selected,
    bulkActions,
    onSelect,
    allIds,
  });

  const footerGroups = table.getFooterGroups();
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizing = table.getState().columnSizing;
  const totalTableWidth = table.getTotalSize();
  const autoExpandedRowsKey = data
    .map(
      (row) =>
        `${row.id}:${row.__subRows?.map((subRow) => subRow.id).join(",") ?? ""}`,
    )
    .join("|");
  const autoExpandedRowsKeyRef = useRef<string>();

  const columnSizeVars = useMemo((): Record<string, number> => {
    const vars: Record<string, number> = {};

    for (const header of table.getFlatHeaders()) {
      vars[getHeaderSizeCssVar(header.id)] = header.getSize();
      vars[getColumnSizeCssVar(header.column.id)] = header.column.getSize();
    }

    vars[getHeaderSizeCssVar(EXPANDER_COLUMN_ID)] = EXPANDER_COLUMN_SIZE;
    vars[getColumnSizeCssVar(EXPANDER_COLUMN_ID)] = EXPANDER_COLUMN_SIZE;

    return vars;
  }, [columnSizing, columnSizingInfo, table]);

  useEffect(() => {
    if (!(initialExpanded || showOnlySubRows) || !hasSubRows) {
      autoExpandedRowsKeyRef.current = undefined;
      return;
    }

    if (autoExpandedRowsKeyRef.current === autoExpandedRowsKey) {
      return;
    }

    autoExpandedRowsKeyRef.current = autoExpandedRowsKey;
    table.toggleAllRowsExpanded(true);
  }, [
    autoExpandedRowsKey,
    hasSubRows,
    initialExpanded,
    showOnlySubRows,
    table,
  ]);

  const shouldFooterRender = table.getFooterGroups().some((group) =>
    group.headers.some((header) => {
      if (header.isPlaceholder) {
        return false;
      }

      return header.column.columnDef.footer !== undefined;
    }),
  );

  const hasVisibleActionsColumn = table
    .getVisibleLeafColumns()
    .some((column) => column.id === ACTIONS_COLUMN_ID);

  const firstVisibleDataColumnId = table
    .getVisibleLeafColumns()
    .find((column) => dataColumnIdsSet.has(column.id))?.id;

  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map((column) => column.id)
    .join("|");

  const columnSizingKey = Object.keys(columnSizing)
    .toSorted()
    .map((columnId) => `${columnId}:${columnSizing[columnId]}`)
    .join("|");

  const bodyCellLayoutKey = `${columnOrder.join("|")}::${visibleColumnIds}::${columnSizingKey}::${
    columnPinning.left?.join("|") ?? ""
  }::${firstVisibleDataColumnId ?? ""}`;

  const bodyInteractionStateKey = `${getBooleanRecordStateKey(
    tanstackBodyStore.expanded,
  )}::${getBooleanRecordStateKey(tanstackBodyStore.rowSelection)}`;

  const bodyCellRenderKey = useMemo(
    () => [data, columns] as const,
    [data, columns],
  );

  const columnsToControl = table
    .getAllLeafColumns()
    .filter((column) => dataColumnIdsSet.has(column.id) && column.getCanHide());

  const shouldShowColumnVisibilityControl =
    enableColumnVisibilityControl && columnsToControl.length > 0;

  const handlePinColumn = (id: string) => {
    const hasDataColumnPinned = tanstackBodyStore.pinnedColumnIds.some(
      (colId) => dataColumnIdsSet.has(colId),
    );

    const nextPinned = [...tanstackBodyStore.pinnedColumnIds];

    if (!hasDataColumnPinned) {
      const systemIdsToAdd = SYSTEM_COLUMN_IDS.filter(
        (sysId) => !nextPinned.includes(sysId),
      );

      nextPinned.unshift(...systemIdsToAdd);
    }

    if (!nextPinned.includes(id)) {
      nextPinned.push(id);
    }

    tanstackBodyStore.setPinnedColumnIds(nextPinned);
  };

  const handleUnpinColumn = (id: string) => {
    const nextPinned = tanstackBodyStore.pinnedColumnIds.filter(
      (columnId) => columnId !== id,
    );
    const hasDataColumnPinned = nextPinned.some((colId) =>
      dataColumnIdsSet.has(colId),
    );

    if (!hasDataColumnPinned) {
      tanstackBodyStore.setPinnedColumnIds(
        nextPinned.filter((colId) => !SYSTEM_COLUMN_IDS.includes(colId)),
      );
      return;
    }

    tanstackBodyStore.setPinnedColumnIds(nextPinned);
  };

  const columnVisibilityControlContent = (
    <ColumnVisibilityControl
      columnsToControl={columnsToControl}
      columnLabelById={columnLabelById}
      isResetDisabled={isResetColumnPreferencesDisabled}
      onResetColumnPreferences={onResetColumnPreferences}
      pinnedColumnIds={tanstackBodyStore.pinnedColumnIds}
      onPinColumn={handlePinColumn}
      onUnpinColumn={handleUnpinColumn}
    />
  );

  const columnVisibilityControlWithAddon = beforeVisibilityControl ? (
    <HStack>
      {beforeVisibilityControl}
      {columnVisibilityControlContent}
    </HStack>
  ) : (
    columnVisibilityControlContent
  );

  const tableSx: TableProps["style"] = {
    ...columnSizeVars,
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    width: `${totalTableWidth}px`,
  };

  return (
    <Box pos="relative" minW="0" maxW="full">
      {shouldShowColumnVisibilityControl && !hasVisibleActionsColumn && (
        <HStack justify="flex-end" mb="3">
          {columnVisibilityControlWithAddon}
        </HStack>
      )}

      <TableContainer
        sx={tableContainerSx}
        overflowX="auto"
        minW="0"
        maxW="full"
        overscrollBehaviorX="contain"
      >
        <Table minW="full" size="sm" style={tableSx}>
          <TableHeader<Row, OrderByColumn>
            table={table}
            dataColumnOrder={dataColumnOrder}
            dataColumnIds={dataColumnIds}
            dataColumnIdsSet={dataColumnIdsSet}
            enableColumnOrdering={enableColumnOrdering}
            enableColumnResizing={enableColumnResizing}
            shouldShowColumnVisibilityControl={
              shouldShowColumnVisibilityControl
            }
            columnVisibilityControlWithAddon={columnVisibilityControlWithAddon}
            orderedBy={orderedBy}
            onOrderByChange={onOrderByChange}
            onDragEnd={tanstackBodyStore.handleDragEnd}
          />

          <TableBody<Row>
            table={table}
            bodyCellLayoutKey={bodyCellLayoutKey}
            bodyInteractionStateKey={bodyInteractionStateKey}
            bodyCellRenderKey={bodyCellRenderKey}
            emptyState={emptyState}
            firstVisibleDataColumnId={firstVisibleDataColumnId}
            showOnlySubRows={showOnlySubRows}
          />

          <TableFooter<Row>
            footerGroups={footerGroups}
            shouldRender={shouldFooterRender}
          />
        </Table>
      </TableContainer>

      {isRowSelectionEnabled && !disableBulkActions && (
        <BulkActions
          store={tanstackBodyStore.bulkActionsStore}
          infoBlock={bulkActionsInfoBlock}
        />
      )}
    </Box>
  );
});

export { TruncatedTextWithTooltip };
